/**
 * Hardware List Component with Login-As-Customer Support
 * Displays all provisioned phones for the customer
 * Works correctly with admin "login as customer" functionality
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  InputAdornment,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Phone as PhoneIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthWithCustomerOverride } from '../../hooks/useAuthWithCustomerOverride';
import { hardwareService } from '../../services/hardwareService';
import type { Hardware } from './types';
import AddHardwareModal from './AddHardwareModal';
import EditHardwareModal from './EditHardwareModal';
import ConfirmDialog from '../Common/ConfirmDialog';
import errorLogger from '../../services/errorLogger.service';

// Create logger for this component
const logger = errorLogger.createComponentLogger('HardwareListAsCustomer');

const HardwareListAsCustomer: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { member, isLoginAsCustomer } = useAuthWithCustomerOverride();

  // Check for login token on initialization
  const urlParams = new URLSearchParams(window.location.search);
  const hasLoginToken = urlParams.has('lat');
  const hasSessionData = sessionStorage.getItem('loginAsCustomerId') && sessionStorage.getItem('loginAsCustomerName');
  const [waitingForLoginAs] = useState(() => {
    const shouldWait = hasLoginToken && !hasSessionData;
    if (shouldWait) {
      logger.debug('Initializing with waiting=true due to login token');
    }
    return shouldWait;
  });

  // Track last fetched customer ID to prevent duplicate fetches
  const [lastFetchedCustomerId, setLastFetchedCustomerId] = useState<number | null>(null);

  // Hardware state
  const [hardware, setHardware] = useState<Hardware[]>([]);
  const [filteredHardware, setFilteredHardware] = useState<Hardware[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Modal states
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedHardwareId, setSelectedHardwareId] = useState<number | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    hardwareId: number | null;
    macAddr: string;
  }>({
    open: false,
    hardwareId: null,
    macAddr: '',
  });

  // Filter hardware based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredHardware(hardware);
    } else {
      const term = searchTerm.toLowerCase();
      setFilteredHardware(
        hardware.filter(
          (h) =>
            h.MacAddr.toLowerCase().includes(term) ||
            h.model.toLowerCase().includes(term) ||
            h.manufacturer.toLowerCase().includes(term) ||
            h.location?.toLowerCase().includes(term) ||
            h.SerialNum?.toLowerCase().includes(term)
        )
      );
    }
  }, [searchTerm, hardware]);

  const loadHardware = async () => {
    // SAFETY CHECK: Don't fetch if waiting for login-as data
    if (waitingForLoginAs) {
      logger.debug('Still waiting for login-as data, aborting fetch');
      return;
    }

    // Verify session data matches member if in login-as mode
    const sessionData = sessionStorage.getItem('loginAsCustomer');
    if (sessionData) {
      try {
        const parsed = JSON.parse(sessionData);
        if (parsed.customerId && parsed.customerId !== member?.customerID) {
          logger.error('CRITICAL: Customer ID mismatch!', {
            sessionCustomerId: parsed.customerId,
            memberCustomerId: member?.customerID
          });
          setError('Customer context mismatch - please refresh the page');
          setLoading(false);
          return;
        }
      } catch (e) {
        logger.error('Error parsing session data:', e);
      }
    }

    try {
      setLoading(true);
      setError(null);

      logger.debug('Fetching hardware with:', {
        isLoginAsCustomer,
        memberCustomerID: member?.customerID,
        memberType: member?.memberType,
      });

      // SECURITY: ALWAYS pass customer ID to prevent data leakage
      let customerId: number | undefined;
      if (member?.customerID) {
        customerId = member.customerID;
        if (isLoginAsCustomer) {
          logger.debug(`LOGIN AS MODE - Using customerID: ${customerId}`);
        } else {
          logger.debug(`CUSTOMER LOGIN - Using customerID: ${customerId}`);
        }
      } else {
        logger.error('SECURITY WARNING: No customer ID available!');
      }

      const data = await hardwareService.getAll(customerId);

      setHardware(data);
      setFilteredHardware(data);

      // Update last fetched customer ID
      if (member?.customerID) {
        setLastFetchedCustomerId(member.customerID);
      }

      logger.debug('Hardware loaded successfully:', {
        count: data.length,
        customerID: member?.customerID
      });
    } catch (err: any) {
      logger.error('Failed to fetch hardware:', err);
      setError(err.message || 'Failed to load hardware');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSuccess = () => {
    setAddModalOpen(false);
    setSuccess('Hardware added successfully');
    loadHardware();
  };

  const handleEditClick = (hardwareId: number) => {
    setSelectedHardwareId(hardwareId);
    setEditModalOpen(true);
  };

  const handleEditClose = () => {
    setEditModalOpen(false);
    setSelectedHardwareId(null);
    loadHardware(); // Reload list in case changes were made
  };

  const handleDeleteClick = (hardwareId: number, macAddr: string) => {
    setDeleteDialog({ open: true, hardwareId, macAddr });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDialog.hardwareId) return;

    try {
      // Pass customerId for login-as mode
      const customerId = member?.customerID;
      await hardwareService.delete(deleteDialog.hardwareId, customerId);
      setSuccess('Hardware deleted successfully');
      setDeleteDialog({ open: false, hardwareId: null, macAddr: '' });
      loadHardware();
    } catch (err: any) {
      setError(err.message || 'Failed to delete hardware');
    }
  };

  const formatMac = (mac: string) => {
    // Format MAC address with colons (001122334455 -> 00:11:22:33:44:55)
    return mac.match(/.{1,2}/g)?.join(':').toUpperCase() || mac;
  };

  // Main effect for loading hardware
  useEffect(() => {
    if (!member) return;

    // Check for login token
    const urlParams = new URLSearchParams(window.location.search);
    const hasLoginToken = urlParams.has('lat');
    const sessionData = sessionStorage.getItem('loginAsCustomer');

    // If we have a login token but no session data, wait
    if (hasLoginToken && !sessionData) {
      logger.debug('Have login token but no session data - waiting...');
      return;
    }

    // If session data exists, verify it matches member
    if (sessionData) {
      try {
        const parsed = JSON.parse(sessionData);
        if (parsed.customerId && parsed.customerId !== member.customerID) {
          logger.debug('Session/member mismatch, waiting for sync...');
          return;
        }
      } catch (e) {
        logger.error('Error parsing session data:', e);
      }
    }

    // Prevent duplicate fetches for same customer
    if (member.customerID === lastFetchedCustomerId) {
      logger.debug('Already fetched for this customer, skipping');
      return;
    }

    // Add small delay to let state settle
    const timer = setTimeout(() => {
      loadHardware();
    }, 200);

    return () => clearTimeout(timer);
  }, [member?.customerID, isLoginAsCustomer]); // Re-fetch when customer ID or login-as state changes

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Hardware Provisioning
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddModalOpen(true)}
          >
            Add Hardware
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadHardware}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Search */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Search by MAC address, model, manufacturer, or location..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {/* Hardware Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>MAC Address</TableCell>
                <TableCell>Manufacturer</TableCell>
                <TableCell>Model</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Line Keys</TableCell>
                <TableCell>Serial #</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredHardware.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <PhoneIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="body1" color="text.secondary">
                      {searchTerm ? 'No hardware found matching your search' : 'No hardware provisioned yet'}
                    </Typography>
                    {!searchTerm && (
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setAddModalOpen(true)}
                        sx={{ mt: 2 }}
                      >
                        Add Your First Phone
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                filteredHardware.map((hw) => (
                  <TableRow
                    key={hw.hardwareID}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleEditClick(hw.hardwareID)}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {formatMac(hw.MacAddr)}
                      </Typography>
                    </TableCell>
                    <TableCell>{hw.manufacturer}</TableCell>
                    <TableCell>{hw.model}</TableCell>
                    <TableCell>{hw.phoneType || 'N/A'}</TableCell>
                    <TableCell>{hw.location || '-'}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Chip label={hw.linekeys} size="small" color="primary" variant="outlined" />
                        {hw.extModAvail && (
                          <Chip label="+40" size="small" color="secondary" variant="outlined" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{hw.SerialNum || '-'}</TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleEditClick(hw.hardwareID)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteClick(hw.hardwareID, hw.MacAddr)}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Summary */}
      {filteredHardware.length > 0 && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Showing {filteredHardware.length} of {hardware.length} devices
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Total Line Keys: {filteredHardware.reduce((sum, hw) => sum + hw.linekeys, 0)}
          </Typography>
        </Box>
      )}

      {/* Modals */}
      <AddHardwareModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSuccess={handleAddSuccess}
        customerId={member?.customerID}
      />

      {editModalOpen && selectedHardwareId && member?.customerID && (
        <EditHardwareModal
          open={editModalOpen}
          onClose={handleEditClose}
          hardwareId={selectedHardwareId}
          customerId={member.customerID}
        />
      )}

      <ConfirmDialog
        open={deleteDialog.open}
        title="Delete Hardware"
        message={`Are you sure you want to delete phone ${formatMac(deleteDialog.macAddr)}? This will remove all associated configuration including accounts and line keys.`}
        confirmText="Delete"
        confirmColor="error"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteDialog({ open: false, hardwareId: null, macAddr: '' })}
      />
    </Box>
  );
};

export default HardwareListAsCustomer;
